import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TFMark } from './TFLogo';
import usePublicTheme from '../hooks/usePublicTheme';

const BOT_URL = 'https://t.me/TrustframeBot';

// ── Theme palette ────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    name: 'dark',
    bg: '#002FA7',           // International Klein Blue
    bgDeep: '#001E6F',
    text: '#ffffff',
    textMuted: 'rgba(255,255,255,0.6)',
    textFaint: 'rgba(255,255,255,0.4)',
    border: 'rgba(255,255,255,0.18)',
    borderFaint: 'rgba(255,255,255,0.10)',
    surface: 'rgba(255,255,255,0.04)',  // translucent panels
    surfaceHover: 'rgba(255,255,255,0.07)',
    accent: '#FF6B1A',        // brighter orange on blue
    accentInk: '#0a0a0a',
    particleLine: 'rgba(255,255,255,0.30)',
    particleDot: 'rgba(255,255,255,0.95)',
  },
  light: {
    name: 'light',
    bg: '#F4F2EC',
    bgDeep: '#E8E4D6',
    text: '#002FA7',
    textMuted: 'rgba(0,47,167,0.65)',
    textFaint: 'rgba(0,47,167,0.40)',
    border: 'rgba(0,47,167,0.20)',
    borderFaint: 'rgba(0,47,167,0.10)',
    surface: 'rgba(0,47,167,0.04)',
    surfaceHover: 'rgba(0,47,167,0.08)',
    accent: '#C45C23',
    accentInk: '#ffffff',
    particleLine: 'rgba(0,47,167,0.40)',
    particleDot: 'rgba(0,47,167,0.85)',
  },
};

// ── Particle canvas hero (theme-aware) ───────────────────────────────────────
function ParticleField({ theme }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let w = 0;
    let h = 0;
    let pts = [];
    let pointer = { x: -9999, y: -9999 };
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.floor((w * h) / 9000);
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
      }));
    };
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      pointer = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { pointer = { x: -9999, y: -9999 }; };

    const parseRGBA = (s) => {
      const m = s.match(/rgba?\(([^)]+)\)/);
      if (!m) return [255, 255, 255, 1];
      const [r, g, b, a] = m[1].split(',').map((v) => parseFloat(v.trim()));
      return [r, g, b, a === undefined ? 1 : a];
    };
    const [lr, lg, lb, la] = parseRGBA(theme.particleLine);

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        const dx = p.x - pointer.x;
        const dy = p.y - pointer.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 14400) {
          p.x += dx * 0.0006;
          p.y += dy * 0.0006;
        }
        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const ddx = p.x - q.x;
          const ddy = p.y - q.y;
          const dist2 = ddx * ddx + ddy * ddy;
          if (dist2 < 13000) {
            const a = (1 - dist2 / 13000) * la;
            ctx.strokeStyle = `rgba(${lr},${lg},${lb},${a})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }
      ctx.fillStyle = theme.particleDot;
      for (const p of pts) ctx.fillRect(p.x - 0.8, p.y - 0.8, 1.6, 1.6);
      raf = requestAnimationFrame(tick);
    };

    resize();
    raf = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [theme]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden />;
}

// ── Marquee ──────────────────────────────────────────────────────────────────
function Marquee({ items, accent = false, reverse = false, dataTestId, theme }) {
  const content = items.join('  ◇  ');
  const repeated = Array.from({ length: 6 }, (_, i) => (
    <span key={i} className="px-6">{content}</span>
  ));
  const style = accent
    ? { background: theme.accent, color: theme.accentInk, borderColor: theme.accent }
    : { background: theme.bgDeep, color: theme.textMuted, borderColor: theme.borderFaint };
  return (
    <div
      data-testid={dataTestId}
      className="relative overflow-hidden border-y py-3 sm:py-4"
      style={style}
    >
      <div className={`flex whitespace-nowrap font-mono text-xs sm:text-sm tracking-[0.25em] uppercase ${reverse ? 'animate-marquee-reverse' : 'animate-marquee'}`}>
        {repeated}{repeated}
      </div>
    </div>
  );
}

// ── Glitch heading (uses Neue Machina / Space Grotesk via .font-machina) ─────
function GlitchHeading({ children, className = '', accent, dark }) {
  return (
    <h1 className={`font-machina relative inline-block ${className}`} data-text={children}>
      <span className="relative z-10">{children}</span>
      <span aria-hidden className={`absolute inset-0 ${dark ? 'mix-blend-screen' : 'mix-blend-multiply'} opacity-0 hover:opacity-100 transition-opacity duration-300`} style={{ color: accent, transform: 'translate(2px, 0)' }}>
        {children}
      </span>
    </h1>
  );
}

// ── Counters ─────────────────────────────────────────────────────────────────
function BlockCounter({ theme }) {
  const [n, setN] = useState(2_847_392);
  useEffect(() => {
    const id = setInterval(() => setN((x) => x + 1), 4200);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="font-mono text-[10px] sm:text-xs tracking-[0.2em]" style={{ color: theme.textMuted }}>
      BLOCK <span style={{ color: theme.accent }}>#{n.toLocaleString()}</span>
    </span>
  );
}

function HashSpinner({ theme }) {
  const [h, setH] = useState('');
  useEffect(() => {
    const gen = () => {
      const chars = '0123456789abcdef';
      let s = '0x';
      for (let i = 0; i < 16; i++) s += chars[Math.floor(Math.random() * 16)];
      setH(s);
    };
    gen();
    const id = setInterval(gen, 1500);
    return () => clearInterval(id);
  }, []);
  return <span className="font-mono text-[10px] sm:text-xs tracking-[0.15em]" style={{ color: theme.textFaint }}>TX {h}</span>;
}

// ── Theme toggle ─────────────────────────────────────────────────────────────
function ThemeToggle({ mode, toggle }) {
  return (
    <button
      data-testid="theme-toggle"
      onClick={toggle}
      className="min-h-[40px] min-w-[40px] px-2 sm:px-3 inline-flex items-center gap-2 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors"
      style={{ borderColor: 'currentColor', color: 'inherit' }}
      aria-label="Toggle theme"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'currentColor' }} />
      <span className="hidden sm:inline">{mode === 'dark' ? 'LIGHT' : 'DARK'}</span>
    </button>
  );
}

// ── Data ─────────────────────────────────────────────────────────────────────
const MODULES = [
  { k: '01', name: 'COMPLY', sub: 'Identity & AML', desc: 'KYC/KYB via Sumsub. Sanctions + UBO checks. PEP screening. GDPR-grade data handling.' },
  { k: '02', name: 'VERIFY', sub: 'Provenance & authenticity', desc: 'Expert authentication. Chain-of-ownership records. Document attestation. Cryptographic certs.' },
  { k: '03', name: 'TRANSACT', sub: 'Escrow & settlement', desc: 'Funds held in escrow. Delivery confirmation gates release. Multi-jurisdiction support.' },
  { k: '04', name: 'ATTEST', sub: 'Ownership records', desc: 'Tamper-evident PDF certs with QR. Public verification URLs. Permanent on-chain anchoring (soon).' },
];

const STATS = [
  { v: '£2.4M+', l: 'In escrow' },
  { v: '847', l: 'Verified accounts' },
  { v: '99.4%', l: 'Dispute-free' },
  { v: '<48h', l: 'Avg. settlement' },
];

const ROLES = [
  { id: 'gallery', name: 'GALLERIES', desc: 'Sell with cleared funds. Reduce chargebacks. Verified buyers only.', perks: ['KYC-cleared collectors', 'Escrow protection', 'Branded certificates', 'Dispute mediation'] },
  { id: 'artist', name: 'ARTISTS', desc: 'Get paid before shipping. Build verified provenance from sale one.', perks: ['Pre-funded transactions', 'Studio ID verification', 'On-chain attestation', 'Royalty tracking (soon)'] },
  { id: 'collector', name: 'COLLECTORS', desc: 'Buy from anywhere with confidence. Hold authenticity certificates that prove out.', perks: ['Money-back guarantee', 'Provenance certificates', 'Independent verification', 'Private collection vault'] },
];

const FEES = [
  { range: '£0 - £5K', fee: '4.5%', tier: 'TIER-01' },
  { range: '£5K - £25K', fee: '3.0%', tier: 'TIER-02' },
  { range: '£25K - £100K', fee: '2.0%', tier: 'TIER-03' },
  { range: '£100K - £500K', fee: '1.25%', tier: 'TIER-04' },
  { range: '£500K+', fee: '0.75%', tier: 'TIER-05' },
];

const TRUST_BLOCKS = [
  { t: 'CRYPTOGRAPHIC', d: 'Every certificate carries a unique signed identifier. Tamper-evident PDF + on-chain anchoring (Q3 2026).', tag: 'SHA-256' },
  { t: 'NON-CUSTODIAL', d: 'Funds held in segregated UK/EU escrow with Tier-1 banking partners. Auditable balances published quarterly.', tag: 'AUDITED' },
  { t: 'PUBLIC', d: 'Any party can verify a certificate at /verify/{TF-ID}. No login required. No central authority needed.', tag: 'OPEN' },
];

// ── Main ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [mode, toggle] = usePublicTheme();
  const theme = THEMES[mode];
  const [activeRole, setActiveRole] = useState('gallery');
  const role = ROLES.find((r) => r.id === activeRole) || ROLES[0];
  const isDark = mode === 'dark';

  // Apply body background so scroll bounce + over-scroll match the theme
  useEffect(() => {
    document.body.style.background = theme.bg;
    document.body.style.backgroundImage = 'none';
    document.documentElement.style.background = theme.bg;
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundImage = '';
      document.documentElement.style.background = '';
    };
  }, [theme]);

  // Re-usable inline styles
  const sec = { borderColor: theme.borderFaint };
  const panel = { background: theme.surface, borderColor: theme.borderFaint, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' };
  const accentBtn = { background: theme.accent, color: theme.accentInk };
  const ghostBtn = { borderColor: theme.border, color: theme.text };

  return (
    <div
      className="min-h-screen overflow-x-hidden relative"
      data-testid="landing-page-v2"
      data-theme={mode}
      style={{ background: theme.bg, color: theme.text }}
    >
      {/* Scanline overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          opacity: isDark ? 0.06 : 0.04,
          backgroundImage: `repeating-linear-gradient(0deg, transparent 0, transparent 2px, ${isDark ? '#fff' : '#002FA7'} 2px, ${isDark ? '#fff' : '#002FA7'} 3px)`,
        }}
        aria-hidden
      />

      {/* NAV */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b"
        style={{ background: `${theme.bg}D9`, borderColor: theme.borderFaint }}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 sm:gap-6 group min-h-[44px]" data-testid="nav-home-link">
            <TFMark color={theme.text} size={26} className="transition-opacity group-hover:opacity-80" />
            <span className="hidden xs:inline font-machina font-bold text-[11px] sm:text-xs uppercase whitespace-nowrap" style={{ letterSpacing: '0.95em', paddingLeft: '0.95em' }}>TRUSTFRAME</span>
          </Link>
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: theme.textMuted }}>
            <a href="#modules" className="hover:opacity-100 opacity-70 transition-opacity">Modules</a>
            <a href="#roles" className="hover:opacity-100 opacity-70 transition-opacity">Roles</a>
            <a href="#fees" className="hover:opacity-100 opacity-70 transition-opacity">Fees</a>
            <a href="#trust" className="hover:opacity-100 opacity-70 transition-opacity">Trust</a>
            <Link
              to="/private"
              data-testid="nav-private-link"
              className="inline-flex items-center gap-1.5 hover:opacity-100 opacity-90 transition-opacity"
              style={{ color: theme.accent }}
            >
              <span className="w-1 h-1 rounded-full" style={{ background: theme.accent }} />
              Private View
            </Link>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle mode={mode} toggle={toggle} />
            <button
              data-testid="nav-wallet-cta"
              onClick={() => alert('Wallet connect launching soon. Use Telegram or email sign-up for now.')}
              className="hidden lg:inline-flex items-center gap-2 px-3 py-2 border min-h-[40px] font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:opacity-80"
              style={{ borderColor: theme.border, color: theme.text }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: theme.accent }} />
              Connect Wallet
            </button>
            <Link
              to="/signup"
              data-testid="nav-signup-cta"
              className="px-3 sm:px-4 py-2 min-h-[40px] inline-flex items-center font-mono text-[10px] tracking-[0.25em] uppercase font-bold transition-colors hover:opacity-90 whitespace-nowrap"
              style={accentBtn}
            >
              Sign Up
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-center pt-20 sm:pt-24" data-testid="hero">
        <ParticleField theme={theme} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-10 sm:py-16">
          <div className="flex items-center justify-between mb-8 sm:mb-12 font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase gap-2 flex-wrap">
            <BlockCounter theme={theme} />
            <span className="hidden sm:inline" style={{ color: theme.textFaint }}>v2.13.0 / mainnet</span>
            <HashSpinner theme={theme} />
          </div>

          <p className="font-mono text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6 break-words" style={{ color: theme.accent }}>
            [ .TRUST.FRAME. // ART.MARKET.PROTOCOL. ]
          </p>

          <GlitchHeading
            accent={theme.accent}
            dark={isDark}
            className="text-[36px] leading-[0.92] sm:text-[72px] md:text-[110px] lg:text-[140px] uppercase mb-6 sm:mb-10 cursor-default break-words"
          >
            Trust,<br/>compiled.
          </GlitchHeading>

          <div className="max-w-2xl mb-10 sm:mb-14">
            <p className="font-mono text-sm sm:text-base leading-relaxed" style={{ color: theme.textMuted }}>
              The protocol for high-value art transactions.<br className="hidden sm:block" />
              Identity verified. Funds escrowed. Provenance attested. Certificates signed.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <Link to="/signup" data-testid="hero-signup-cta" className="group flex items-center justify-center gap-3 px-6 sm:px-8 py-4 min-h-[52px] font-machina font-bold text-xs tracking-[0.3em] uppercase transition-all hover:opacity-90 hover:translate-x-1" style={accentBtn}>
              <span>Initiate Transaction</span>
              <span className="font-mono">→</span>
            </Link>
            <a href={BOT_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-telegram-cta" className="group flex items-center justify-center gap-3 px-6 sm:px-8 py-4 min-h-[52px] border font-machina font-bold text-xs tracking-[0.3em] uppercase transition-colors hover:opacity-80" style={ghostBtn}>
              <span>./TELEGRAM</span>
              <span className="font-mono">⌘</span>
            </a>
          </div>

          <div className="mt-12 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px border" style={{ background: theme.borderFaint, borderColor: theme.borderFaint }}>
            {STATS.map((s) => (
              <div key={s.l} className="p-4 sm:p-6" style={panel}>
                <p className="font-machina font-bold text-2xl sm:text-3xl lg:text-4xl break-words" style={{ color: theme.text }}>{s.v}</p>
                <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.25em] uppercase mt-1 sm:mt-2 break-words" style={{ color: theme.textMuted }}>{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee 1 */}
      <Marquee
        dataTestId="marquee-1"
        accent
        theme={theme}
        items={['COMPLY', 'VERIFY', 'TRANSACT', 'ATTEST', 'COMPLY', 'VERIFY', 'TRANSACT', 'ATTEST']}
      />

      {/* MODULES */}
      <section id="modules" className="relative py-16 sm:py-24 md:py-32 border-b" style={sec} data-testid="modules">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: theme.accent }}>[ .04. .MODULES. ]</p>
          <h2 className="font-machina font-bold text-3xl sm:text-5xl md:text-6xl uppercase mb-10 sm:mb-16 md:mb-20 max-w-3xl leading-[0.95] break-words">
            Four primitives.<br />One protocol.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px border" style={{ background: theme.borderFaint, borderColor: theme.borderFaint }}>
            {MODULES.map((m) => (
              <div key={m.k} data-testid={`module-${m.k}`} className="group p-6 sm:p-8 lg:p-10 transition-colors relative" style={panel}>
                <div className="flex items-start justify-between mb-6 sm:mb-8">
                  <span className="font-mono text-[10px] tracking-[0.3em]" style={{ color: theme.textFaint }}>{m.k}</span>
                  <span className="w-2 h-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: theme.accent }} />
                </div>
                <h3 className="font-machina font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mb-2 break-words group-hover:opacity-80 transition-opacity">
                  <span style={{ color: theme.text }}>{m.name}</span>
                </h3>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-5" style={{ color: theme.textFaint }}>{'// '}{m.sub}</p>
                <p className="font-mono text-sm leading-relaxed max-w-md break-words" style={{ color: theme.textMuted }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section id="roles" className="relative py-16 sm:py-24 md:py-32 border-b" style={sec} data-testid="roles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: theme.accent }}>[ .03. .ROLES. ]</p>
          <h2 className="font-machina font-bold text-3xl sm:text-5xl md:text-6xl uppercase mb-10 sm:mb-12 md:mb-16 max-w-3xl leading-[0.95] break-words">Who is this for.</h2>

          <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-12">
            {ROLES.map((r) => (
              <button
                key={r.id}
                onClick={() => setActiveRole(r.id)}
                data-testid={`role-tab-${r.id}`}
                className="px-4 sm:px-5 py-2.5 min-h-[44px] font-mono text-[10px] tracking-[0.3em] uppercase transition-all"
                style={
                  activeRole === r.id
                    ? { background: theme.accent, color: theme.accentInk }
                    : { border: `1px solid ${theme.border}`, color: theme.textMuted, background: 'transparent' }
                }
              >
                {r.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px border" style={{ background: theme.borderFaint, borderColor: theme.borderFaint }} data-testid={`role-panel-${role.id}`}>
            <div className="lg:col-span-7 p-6 sm:p-10 lg:p-14" style={panel}>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: theme.textFaint }}>{'// '}{role.name}</p>
              <h3 className="font-machina font-bold text-2xl sm:text-4xl md:text-5xl uppercase mb-6 leading-[0.95] break-words">{role.desc}</h3>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/signup" className="px-5 py-3 min-h-[48px] inline-flex items-center justify-center font-mono text-[10px] tracking-[0.3em] uppercase font-bold transition-colors hover:opacity-90 text-center" style={accentBtn}>Sign Up Online →</Link>
                <a href={BOT_URL} target="_blank" rel="noopener noreferrer" className="px-5 py-3 min-h-[48px] inline-flex items-center justify-center border font-mono text-[10px] tracking-[0.3em] uppercase transition-colors hover:opacity-80 text-center" style={ghostBtn}>./TELEGRAM</a>
              </div>
            </div>
            <div className="lg:col-span-5 p-6 sm:p-10 lg:p-14" style={panel}>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-6" style={{ color: theme.accent }}>[ .INCLUDES. ]</p>
              <ul className="space-y-4">
                {role.perks.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 group">
                    <span className="font-mono text-[10px] mt-1 tracking-widest flex-shrink-0" style={{ color: theme.accent }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-mono text-sm break-words" style={{ color: theme.text }}>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee 2 */}
      <Marquee
        dataTestId="marquee-2"
        reverse
        theme={theme}
        items={['AUDITED', 'KYC + AML', 'GDPR', 'NON-CUSTODIAL ESCROW (Q3)', 'PUBLIC ATTESTATION', 'OPEN PROTOCOL']}
      />

      {/* FEES */}
      <section id="fees" className="relative py-16 sm:py-24 md:py-32 border-b" style={sec} data-testid="fees">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: theme.accent }}>[ .02. .FEE.SCHEDULE. ]</p>
          <h2 className="font-machina font-bold text-3xl sm:text-5xl md:text-6xl uppercase mb-10 sm:mb-12 md:mb-16 max-w-3xl leading-[0.95] break-words">
            Transparent fees.<br/>No surprises.
          </h2>

          <div className="border" style={{ borderColor: theme.borderFaint }}>
            {FEES.map((row, i, arr) => (
              <div
                key={row.tier}
                className={`grid grid-cols-3 sm:grid-cols-4 items-center px-4 sm:px-8 py-5 sm:py-7 transition-colors group ${i < arr.length - 1 ? 'border-b' : ''}`}
                style={{ borderColor: theme.borderFaint, ...panel }}
              >
                <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase transition-colors" style={{ color: theme.textFaint }}>{row.tier}</span>
                <span className="font-mono text-sm sm:text-base col-span-1 sm:col-span-2 break-words" style={{ color: theme.text }}>{row.range}</span>
                <span className="font-machina font-bold text-2xl sm:text-3xl lg:text-4xl text-right" style={{ color: theme.text }}>{row.fee}</span>
              </div>
            ))}
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase mt-6" style={{ color: theme.textFaint }}>
            {'// '}Fees apply on settlement only. Verification and identity checks are included.
          </p>
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="relative py-16 sm:py-24 md:py-32 border-b" style={sec} data-testid="trust">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: theme.accent }}>[ .01. .TRUST.LAYER. ]</p>
          <h2 className="font-machina font-bold text-3xl sm:text-5xl md:text-6xl uppercase mb-10 sm:mb-12 md:mb-16 max-w-3xl leading-[0.95] break-words">
            Verifiable.<br/>End to end.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px border" style={{ background: theme.borderFaint, borderColor: theme.borderFaint }}>
            {TRUST_BLOCKS.map((b) => (
              <div key={b.t} className="p-6 sm:p-10 group transition-colors" style={panel}>
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[10px] tracking-[0.25em]" style={{ color: theme.accent }}>{b.tag}</span>
                  <span className="w-2 h-2 rounded-full border transition-colors group-hover:bg-current" style={{ borderColor: theme.accent, color: theme.accent }} />
                </div>
                <h3 className="font-machina font-bold text-2xl sm:text-3xl uppercase mb-4 break-words">{b.t}</h3>
                <p className="font-mono text-sm leading-relaxed break-words" style={{ color: theme.textMuted }}>{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 sm:py-24 md:py-32 border-b" style={sec} data-testid="final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase mb-6" style={{ color: theme.accent }}>[ .READY. ]</p>
          <h2 className="font-machina font-bold text-3xl sm:text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.95] mb-8 sm:mb-12 break-words">
            Start your<br/>first transaction.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-center">
            <Link to="/signup" data-testid="cta-signup" className="w-full sm:w-auto px-8 py-4 min-h-[52px] inline-flex items-center justify-center font-machina font-bold text-xs tracking-[0.3em] uppercase transition-colors hover:opacity-90" style={accentBtn}>
              Sign Up Online →
            </Link>
            <a href={BOT_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-telegram" className="w-full sm:w-auto px-8 py-4 min-h-[52px] inline-flex items-center justify-center border font-machina font-bold text-xs tracking-[0.3em] uppercase transition-colors hover:opacity-80" style={ghostBtn}>
              ./TELEGRAM
            </a>
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase mt-8" style={{ color: theme.textFaint }}>
            {'// '}~5 min to verify · 0 setup fees · No card needed
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div className="col-span-2">
              <div className="flex items-center gap-4 sm:gap-6 mb-4">
                <TFMark color={theme.text} size={28} />
                <span className="font-machina font-bold text-xs sm:text-sm uppercase" style={{ letterSpacing: '0.95em', paddingLeft: '0.95em' }}>TRUSTFRAME</span>
              </div>
              <p className="font-mono text-xs leading-relaxed max-w-sm break-words" style={{ color: theme.textMuted }}>
                The protocol for high-value art transactions. Built in London. Audited globally.
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: theme.textFaint }}>PROTOCOL</p>
              <ul className="space-y-3 font-mono text-xs" style={{ color: theme.textMuted }}>
                <li><a href="#modules" className="hover:opacity-100 opacity-90 transition-opacity">Modules</a></li>
                <li><a href="#fees" className="hover:opacity-100 opacity-90 transition-opacity">Fees</a></li>
                <li><a href="#trust" className="hover:opacity-100 opacity-90 transition-opacity">Trust layer</a></li>
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: theme.textFaint }}>CONTACT</p>
              <ul className="space-y-3 font-mono text-xs" style={{ color: theme.textMuted }}>
                <li><a href="https://t.me/TrustframeBot" className="hover:opacity-100 opacity-90 transition-opacity">@TrustframeBot</a></li>
                <li><a href="mailto:hello@trustframe.legal" className="hover:opacity-100 opacity-90 transition-opacity break-all">hello@trustframe.legal</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ borderColor: theme.borderFaint, color: theme.textFaint }}>
            <p>© 2026 INFINITYPAX AGENCY LTD. ALL RIGHTS RESERVED.</p>
            <p className="flex items-center gap-3 flex-wrap">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: theme.accent }} />
              <span>SYSTEM OPERATIONAL</span>
              <BlockCounter theme={theme} />
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
